document.addEventListener("alpine:init", () => {
  Alpine.data("cartDrawer", (options) => ({
    open: false,
    cart: { items: [] },
    customer: null,
    isUpdating: false,
    goals: null,
    mixMatchQuantity: null,
    mixMatchTiers: [
      { quantity: 6, discount: 4 },
      { quantity: 12, discount: 6 },
      { quantity: 24, discount: 8 },
    ],
    sampleOfferThreshold: 4000,
    sampleOfferMax: 3,
    sampleOfferProductId: 15504923656567,
    sampleOfferCheckoutMessage: null,
    isNormalizingSamples: false,

    init() {
      try {
        this.cart.items = JSON.parse(this.$refs.cartJSON.innerHTML).items;

        if (this.$refs.cartJSON) {
          console.log("Parsed initial cart:", this.cart);
        } else {
          this.cart = { items: [], total_price: 0, item_count: 0 };
        }

        if (this.$refs.customerJSON) {
          this.customer = JSON.parse(this.$refs.customerJSON.innerHTML);
        }

        if (this.$refs.progressJSON) {
          this.goals = JSON.parse(this.$refs.progressJSON.textContent);
          this.goals.sort((a, b) => a.threshold - b.threshold);
        }

        if (this.customer.id != null) {
          this.fetchCreditBalance();
        }

        const params = new URLSearchParams(window.location.search);
        if (params.get("cart") === "open") {
          this.open = true;
        }

        if (params.get("variantAdd")) {
          const variantId = params.get("variantAdd");

          if (!this.isInCart(parseInt(variantId))) {
            this.addToCart(variantId, 1).then(() => {
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.delete("variantAdd");
              window.history.replaceState({}, "", newUrl);
            });
          }
        }

        this.fetchCart();
        this.getCreditEarned();

        document.addEventListener("cart:drawer:open", () => {
          this.fetchCart();
          this.open = true;
        });

        document.addEventListener("cart:drawer:close", () => {
          this.open = false;
        });

        this.$watch("mixMatchQuantity", () => {
          window.dispatchEvent(
            new CustomEvent("mixmatch:updated", {
              bubbles: true,
              detail: { quantity: this.mixMatchQuantity },
            })
          );
        });

        this.$watch("cart", () => {
          window.dispatchEvent(
            new CustomEvent("cart:updated", {
              bubbles: true,
              detail: { cart: this.cart },
            })
          );
          if (this.cart) {
            this.getMixMatch();
            this.getCreditEarned();
            this.getProgressBar();
            this.handleGoalUpdates();
          }
        });
      } catch (error) {
        console.error("Error in init:", error);
        this.cart = { items: [], total_price: 0, item_count: 0 };
      }
    },

    async fetchCart() {
      try {
        const response = await fetch("/cart?view=json");
        const cartJSON = await response.json();

        if (cartJSON?.cart) {
          this.cart = cartJSON.cart;

          if (Array.isArray(this.cart.items)) {
            this.cart.items.forEach((item) => {
              const variantSellingPlans =
                cartJSON.selling_plans[item.variant_id];

              if (variantSellingPlans) {
                item.selling_plans = variantSellingPlans;
              }

              const maxAvailable = cartJSON.max_available[item.variant_id];

              if (maxAvailable) {
                item.max_available = maxAvailable;
              }
            });
          }

          await this.enforceSampleOfferRules();
        } else {
          this.cart = {
            items: [],
            total_price: 0,
            item_count: 0,
          };
        }
      } catch (error) {
        console.error("Error fetching cart:", error);
        this.cart = {
          items: [],
          total_price: 0,
          item_count: 0,
        };
      }
    },

    closeDrawer() {
      this.open = false;
    },

    async updateLine(
      lineItem,
      newQuantity = lineItem.quantity,
      subscribe = false
    ) {
      if (this.isUpdating) return;

      this.isUpdating = true;

      if (this.isSampleOffer(lineItem) && parseInt(newQuantity) > 1) {
        newQuantity = 1;
      }

      if (lineItem.max_available && newQuantity > lineItem.max_available) {
        lineItem.error = "You already have all the available quantity in your cart";
        setTimeout(() => {
          lineItem.error = null;
        }, 3000);
        this.isUpdating = false;
        return; 
      }

      const requestBody = {
        id: lineItem.key,
        quantity: parseInt(newQuantity),
      };

      if (subscribe) {
        requestBody.selling_plan =
          lineItem.selling_plans.groups[0].selling_plans[0].id;
      }

      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || errorData.message || "Error updating cart");
        }

        await this.fetchCart();
      } catch (error) {
        lineItem.error = error.message;
        setTimeout(() => {
          lineItem.error = null;
        }, 3000);
        console.error(error);
      } finally {
        this.isUpdating = false;
      }
    },

    async removeItem(lineItem) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: lineItem.key,
            quantity: 0,
          }),
        });

        if (!response.ok) throw new Error("Network response was not ok");

        // const updatedCart = await response.json();
        // this.cart = updatedCart;
        await this.fetchCart();
      } catch (error) {
        console.error("Error removing item:", error);
      } finally {
        this.isUpdating = false;
      }
    },

    async addToCart(variantId, quantity = 1, properties = {}) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: quantity,
                properties: properties,
              },
            ],
          }),
        });

        if (!response.ok) throw new Error("Network response was not ok");

        await this.fetchCart();
      } catch (error) {
        console.error("Error adding item to cart:", error);
      } finally {
        this.isUpdating = false;
      }
    },

    getMixMatch() {
      const totalMixMatchQuantity =
        this.cart?.items?.reduce(
          (sum, item) =>
            sum + (item.properties?._mix_match === "true" ? item.quantity : 0),
          0
        ) || 0;

      this.mixMatchQuantity = totalMixMatchQuantity;
    },

    isInCart(variantId) {
      return Boolean(
        this.cart?.items?.some(
          (item) => Number(item.variant_id) === Number(variantId)
        )
      );
    },

    isUpsell(item) {
      const variantId = item.variant_id;
      const sku = item.sku || "";
      const type = item.product_type || "";

      const isPropertyUpsell = this.cart.items.find(
        (cartItem) =>
          cartItem.variant_id == variantId &&
          cartItem.properties?._upsell === "true"
      );

      const isTasterSku = sku.includes("TASTER");
      const isUpsellType = type.includes("Upsell");

      return isPropertyUpsell || isTasterSku || isUpsellType;
    },

    isGift(item) {
      const variantId = item.variant_id;

      return this.cart.items.find(
        (item) =>
          item.variant_id === variantId &&
          item.properties?._free_gift === "true"
      );
    },

    isSampleOffer(item) {
      return (
        String(item?.properties?._xmiles_free_sample) === "true" ||
        Number(item?.product_id) === this.sampleOfferProductId
      );
    },

    parseSampleVariantTitle(variantTitle, fallbackTitle = "Sample") {
      const parts = String(variantTitle || "")
        .split("/")
        .map((part) => part.trim())
        .filter(Boolean);

      if (parts.length >= 3) {
        return {
          brand: parts[0],
          title: parts[1],
          flavour: parts.slice(2).join(" / "),
        };
      }

      if (parts.length === 2) {
        return {
          brand: parts[0],
          title: parts[1],
          flavour: "",
        };
      }

      return {
        brand: "",
        title: fallbackTitle,
        flavour:
          variantTitle && variantTitle !== "Default Title" ? variantTitle : "",
      };
    },

    getSampleOfferItemTitle(item) {
      return this.parseSampleVariantTitle(
        item?.variant_title,
        item?.product_title || "Sample"
      ).title;
    },

    getSampleOfferItemMeta(item) {
      const sample = this.parseSampleVariantTitle(
        item?.variant_title,
        item?.product_title || "Sample"
      );

      return [sample.brand, sample.flavour].filter(Boolean).join(" / ");
    },

    getSampleOfferItems() {
      return this.cart?.items?.filter((item) => this.isSampleOffer(item)) || [];
    },

    getRegularCartItems() {
      return this.cart?.items?.filter((item) => !this.isSampleOffer(item)) || [];
    },

    getSampleOfferLimit(sampleItems = this.getSampleOfferItems()) {
      const configuredLimit = parseInt(sampleItems[0]?.properties?._sample_offer_limit);
      return Math.min(configuredLimit || this.sampleOfferMax, this.sampleOfferMax);
    },

    getSampleOfferThreshold(sampleItems = this.getSampleOfferItems()) {
      return parseInt(sampleItems[0]?.properties?._sample_offer_threshold) || this.sampleOfferThreshold;
    },

    getSampleOfferRemaining() {
      return Math.max(this.getSampleOfferThreshold() - this.getQualifyingTotal(), 0);
    },

    isSampleOfferUnlocked() {
      return this.getSampleOfferRemaining() === 0;
    },

    getSampleOfferDrawerMessage() {
      if (this.isSampleOfferUnlocked()) {
        return "Unlocked - your selected samples are free on this order.";
      }

      return `Add ${this.formatMoney(this.getSampleOfferRemaining(), "\u00a3{{amount}}")} more to make these samples free.`;
    },

    async enforceSampleOfferRules() {
      if (this.isNormalizingSamples || !this.cart?.items?.length) return;

      const sampleItems = this.getSampleOfferItems();
      if (!sampleItems.length) return;

      const sampleOfferLimit = this.getSampleOfferLimit(sampleItems);
      const changes = [];

      sampleItems.forEach((item, index) => {
        if (index >= sampleOfferLimit) {
          changes.push({ item, quantity: 0 });
        } else if (parseInt(item.quantity) > 1) {
          changes.push({ item, quantity: 1 });
        }
      });

      if (!changes.length) return;

      this.isNormalizingSamples = true;

      try {
        for (const change of changes) {
          const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: change.item.key,
              quantity: change.quantity,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || errorData.message || "Error updating sample items");
          }
        }

        await this.fetchCart();
      } catch (error) {
        console.error("Error enforcing sample offer rules:", error);
      } finally {
        this.isNormalizingSamples = false;
      }
    },

    async removeSampleOfferItems(sampleItems = this.getSampleOfferItems()) {
      if (!sampleItems.length) return false;

      this.isNormalizingSamples = true;

      try {
        for (const item of sampleItems) {
          const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || errorData.message || "Error removing sample items");
          }
        }

        return true;
      } finally {
        this.isNormalizingSamples = false;
      }
    },

    async validateSampleOfferCheckout() {
      await this.fetchCart();

      const sampleItems = this.getSampleOfferItems();
      if (!sampleItems.length || this.isSampleOfferUnlocked()) {
        this.sampleOfferCheckoutMessage = null;
        return true;
      }

      const threshold = this.getSampleOfferThreshold(sampleItems);
      await this.removeSampleOfferItems(sampleItems);
      await this.fetchCart();

      this.sampleOfferCheckoutMessage =
        `Free samples are only available on orders over ${this.formatMoney(threshold, "\u00a3{{amount}}")}, excluding sample products. We have removed them from your cart.`;
      this.open = true;

      return false;
    },

    isMixAndMatch(item) {
      const variantId = item.variant_id;

      return this.cart.items.find(
        (item) =>
          item.variant_id === variantId &&
          item.properties?._mix_match === "true"
      );
    },

    isBicarbBowl(item) {
      const variantId = item.variant_id;

      if (variantId === 55650539110775) {
        const bicarbItems = this.cart.items.filter(
          (cartItem) =>
            cartItem.product_id === 8004114710690 &&
            cartItem.variant_id !== 55650539110775
        );

        const totalBicarbQuantity = bicarbItems.reduce(
          (sum, currentItem) => sum + currentItem.quantity,
          0
        );

        if (item.quantity != totalBicarbQuantity) {
          this.updateLine(item, totalBicarbQuantity);
        }

        return true;
      }
    },

    async fetchCreditBalance() {
      try {
        const url = `https://application.rise-ai.com/v2/gift-cards/?customer_id=${this.customer.id}&shop_url=xmiles.myshopify.com`;
        const accessToken = "kF8k86p7MTxZLjHgohrnExrvzgq8ISLBNOPsO0nUjZ6";

        let result = await fetch(url, {
          method: "GET",
          headers: {
            "x-rise-access-token": accessToken,
          },
        }).then((res) => res.json());

        this.customer.credit = result.amount * 100;
      } catch (error) {
        console.error("Error fetching credit balance:", error);
      }
    },

    getCreditEarned() {
      let credit_earned;

      if (this.customer.id === null) {
        credit_earned = this.cart.total_price * 0.1;
      } else if (this.customer.tags.includes("xmiler")) {
        credit_earned = this.cart.total_price * 0.015;
      } else if (this.customer.tags.includes("xpro")) {
        credit_earned = this.cart.total_price * 0.02;
      } else if (this.customer.tags.includes("xelite")) {
        credit_earned = this.cart.total_price * 0.025;
      } else if (this.customer.tags.includes("active-xplus-sub")) {
        credit_earned = this.cart.total_price * 0.05;
      } else {
        credit_earned = this.cart.total_price * 0.01;
      }

      this.cart.credit_earned = credit_earned;
    },

    getQualifyingTotal() {
      return this.cart.items
        .filter((item) => 
          String(item.product_type || "").toLowerCase() !== "gift card" &&
          item.handle !== "xmiles-plus" &&
          !this.isSampleOffer(item)
        )
        .reduce((sum, item) => sum + item.final_line_price, 0);
    },

    getProgressBar() {
      if (!this.goals?.length) return 0;

      const cartValue = this.getQualifyingTotal();
      const totalSteps = this.goals.length + 1;
      const stepSize = 100 / totalSteps;
      const maxThreshold = Math.max(...this.goals.map((g) => g.threshold));

      if (cartValue >= maxThreshold) {
        return 100;
      }

      if (cartValue < this.goals[0].threshold) {
        return (cartValue / this.goals[0].threshold) * stepSize;
      }

      let currentGoal = this.goals[0].threshold;
      let previousGoal = 0;
      let currentPosition = stepSize;

      for (let i = 0; i < this.goals.length; i++) {
        if (cartValue >= this.goals[i].threshold) {
          currentGoal =
            i < this.goals.length - 1
              ? this.goals[i + 1].threshold
              : maxThreshold;
          previousGoal = this.goals[i].threshold;
          currentPosition = stepSize * (i + 2);
        } else {
          currentGoal = this.goals[i].threshold;
          break;
        }
      }

      const segmentProgress =
        (cartValue - previousGoal) / (currentGoal - previousGoal);
      const previousPosition = currentPosition - stepSize;
      const progressWidth = previousPosition + segmentProgress * stepSize;

      return Math.min(100, Math.max(0, progressWidth));
    },

    async handleGoalUpdates() {
      if (!this.cart?.items || !this.goals?.length || this.isUpdating) return;

      const goalsWithVariants = this.goals.filter((goal) => goal.variant_id);
      if (!goalsWithVariants.length) return;

      const cartValue = this.getQualifyingTotal();

      for (const goal of goalsWithVariants) {
        const variantId = parseInt(goal.variant_id);
        const isReached = cartValue >= goal.threshold;

        const freeGiftItem = this.cart.items.find(
          (item) =>
            item.variant_id === variantId &&
            item.properties?._free_gift === "true"
        );

        if (isReached) {
          if (!freeGiftItem) {
            await this.addToCart(variantId, 1, { _free_gift: "true" });
          }
        } else if (freeGiftItem) {
          await this.removeItem(freeGiftItem);
        }
      }
    },

    isMilestoneReached(threshold) {
      return this.cart.total_price >= threshold;
    },

    getMilestonePosition(index) {
      const totalSteps = this.goals.length + 1;
      const stepSize = 100 / totalSteps;
      return stepSize * (index + 1);
    },

    async checkout() {
      if (this.isUpdating) {
        await new Promise((resolve) => {
          const checkUpdating = () => {
            if (!this.isUpdating) {
              resolve();
            } else {
              setTimeout(checkUpdating, 100);
            }
          };
          checkUpdating();
        });
      }

      const canCheckout = await this.validateSampleOfferCheckout();
      if (!canCheckout) return;

      window.location.href = "/checkout";
    },

    formatMoney(money, format = "${{amount}}") {
      if (typeof money === "string") money = money.replace(".", "");

      const value = parseInt(money || 0, 10);
      const dollars = Math.floor(value / 100);
      const cents = (value % 100).toString().padStart(2, "0");

      return format
        .replace("{{amount}}", dollars + "." + cents)
        .replace("{{amount_no_decimals}}", dollars)
        .replace("{{amount_with_comma_separator}}", dollars + "," + cents)
        .replace(
          "{{amount_no_decimals_with_comma_separator}}",
          dollars.toLocaleString()
        );
    },

    hasOnlyGiftCards() {
      return this.cart.items.every((item) => item.product_type === "Gift Card");
    },

    hasAnyErrors() {
      return this.cart.items.some(item => item.error != null);
    }
  }));
});
